// Input handling for keyboard and touch controls
export class Input {
  private keys: Map<string, boolean> = new Map();
  private keysPressed: Set<string> = new Set(); // For one-time key presses
  private canvas: HTMLCanvasElement;
  
  // Touch controls for mobile
  public touchLeft: boolean = false;
  public touchRight: boolean = false;
  public touchFire: boolean = false;
  // Analog touch axis from virtual joystick (-1..1)
  public touchAxis: { x: number; y: number } = { x: 0, y: 0 };
  
  // Click/touch coordinates
  private clickX: number = -1;
  private clickY: number = -1;
  private hasClick: boolean = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.setupKeyboard();
    this.setupTouch();
    this.setupMouse();
  }

  private setupKeyboard(): void {
    window.addEventListener('keydown', (e) => {
      this.keys.set(e.key.toLowerCase(), true);
      this.keysPressed.add(e.key.toLowerCase());
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
        e.preventDefault();
      }
    });

    window.addEventListener('keyup', (e) => {
      this.keys.set(e.key.toLowerCase(), false);
    });
  }

  private setupTouch(): void {
    // Touch controls will be added for mobile support
    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      
      // Check for tap on canvas (for button clicks)
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;
        this.clickX = (touch.clientX - rect.left) * scaleX;
        this.clickY = (touch.clientY - rect.top) * scaleY;
        this.hasClick = true;
      }
      
      this.handleTouch(e.touches);
    });

    this.canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      this.handleTouch(e.touches);
    });

    this.canvas.addEventListener('touchend', () => {
      this.touchLeft = false;
      this.touchRight = false;
      this.touchFire = false;
      this.touchAxis.x = 0;
      this.touchAxis.y = 0;
    });
  }
  
  private setupMouse(): void {
    this.canvas.addEventListener('click', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      const scaleX = this.canvas.width / rect.width;
      const scaleY = this.canvas.height / rect.height;
      this.clickX = (e.clientX - rect.left) * scaleX;
      this.clickY = (e.clientY - rect.top) * scaleY;
      this.hasClick = true;
    });
  }

  private handleTouch(touches: TouchList): void {
    for (let i = 0; i < touches.length; i++) {
      const touch = touches[i];
      const x = touch.clientX;
      const rect = this.canvas.getBoundingClientRect();
      
      // legacy quadrant touch control: left / right / fire
      if (x < rect.width / 3) {
        this.touchLeft = true;
        this.touchAxis.x = -1;
      } else if (x > (2 * rect.width) / 3) {
        this.touchRight = true;
        this.touchAxis.x = 1;
      } else {
        this.touchFire = true;
        this.touchAxis.x = 0;
      }
      // vertical axis can be derived from touch Y relative to canvas center
      const cy = rect.top + rect.height / 2;
      const dy = touch.clientY - cy;
      // normalize roughly to -1..1 based on half-height
      this.touchAxis.y = Math.max(-1, Math.min(1, dy / (rect.height / 2)));
    }
  }

  public isDown(key: string): boolean {
    return this.keys.get(key.toLowerCase()) || false;
  }

  public get left(): boolean {
    return this.isDown('arrowleft') || this.isDown('a') || this.touchLeft || (this.touchAxis.x < -0.2);
  }

  public get right(): boolean {
    return this.isDown('arrowright') || this.isDown('d') || this.touchRight || (this.touchAxis.x > 0.2);
  }

  public get up(): boolean {
    return this.isDown('arrowup') || this.isDown('w') || (this.touchAxis.y < -0.2);
  }

  public get down(): boolean {
    return this.isDown('arrowdown') || this.isDown('s') || (this.touchAxis.y > 0.2);
  }

  public get fire(): boolean {
    return this.isDown(' ') || this.isDown('z') || this.touchFire;
  }

  public get escape(): boolean {
    return this.isDown('escape');
  }

  public get enter(): boolean {
    return this.isDown('enter');
  }

  public get tab(): boolean {
    return this.isDown('tab');
  }

  public isNumberPressed(num: number): boolean {
    const key = num.toString();
    const pressed = this.keysPressed.has(key);
    if (pressed) {
      this.keysPressed.delete(key);
      console.log('Weapon key pressed:', num);
    }
    return pressed;
  }
  
  public getClick(): { x: number; y: number } | null {
    if (this.hasClick) {
      this.hasClick = false;
      return { x: this.clickX, y: this.clickY };
    }
    return null;
  }
}
